/**
 * Лаборатория переходов.
 *
 * Временный экран: он существует ровно до того момента, когда мы выберем,
 * что оставить основным, что для особых случаев, а что убрать. Поэтому
 * здесь можно то, чего нельзя в остальном приложении, — например, показывать
 * пользователю слово «механизм».
 *
 * Смысл в том, чтобы выбирать глазами и на телефоне, а не по описаниям
 * в переписке. Каждый механизм прогоняется на трёх площадях сразу: у бейджа,
 * блока и карты они ведут себя по-разному, и «хорошо на блоке» ничего
 * не говорит про «хорошо на карте».
 */

import { useCallback, useState } from 'react'

import { Card } from '@taro/cards'
import { Button, Stack, Surface, Text } from '@taro/design'
import { MECHANISMS } from '@taro/ink'
import { InkReveal, useInk } from '@taro/ink/react'
import { haptic } from '@taro/tg'

import styles from './Lab.module.css'

import type { Scope } from '@taro/ink'

const SPEEDS: Array<[string, number]> = [
  ['Быстро', 0.75],
  ['Базово', 1],
  ['Ритуал', 1.7],
]

const SCOPES: Array<[Scope, string]> = [
  ['screen', 'Экран'],
  ['object', 'Объект'],
]

export function Lab() {
  const ink = useInk()

  const [mechanism, setMechanism] = useState<string | undefined>(undefined)
  const [scope, setScope] = useState<Scope>('screen')
  const [speed, setSpeed] = useState(1)
  const [seed, setSeed] = useState(ink.seed)
  const [shown, setShown] = useState(true)
  // Ключ перезапускает поддерево: без него повторный показ того же самого
  // содержимого не перевыбирает форму, и «проиграть ещё раз» показывает
  // ту же кляксу.
  const [take, setTake] = useState(0)

  const replay = useCallback(() => {
    haptic('tap')
    setShown(false)
  }, [])

  const reseed = useCallback(() => {
    haptic('select')
    setSeed(ink.reseed())
    // Ключ здесь НЕ трогается намеренно. Смена ключа перемонтирует блоки,
    // те стартуют уже скрытыми, уход не проигрывается — и onHidden,
    // на котором держится весь цикл, не срабатывает никогда. Экран замирал
    // именно из-за этого. Новый ключ выдаст onHidden, когда уход доиграет.
    setShown(false)
  }, [ink])

  const onHidden = useCallback(() => {
    setTake((t) => t + 1)
    setShown(true)
  }, [])

  return (
    <Stack gap={6}>
      <Surface level="base" className={styles.panel}>
        <Stack gap={5}>
          <Stack gap={2}>
            <Text variant="overline" tone="sacred">
              Механизм
            </Text>
            <div className={styles.chips}>
              <Chip active={mechanism === undefined} onClick={() => setMechanism(undefined)}>
                Случайно
              </Chip>
              {MECHANISMS.filter((m) => !m.parked).map((m) => (
                <Chip
                  key={m.id}
                  active={mechanism === m.id}
                  onClick={() => setMechanism(m.id)}
                  title={m.note}
                >
                  {m.title}
                </Chip>
              ))}
            </div>

            <Text variant="caption" tone="tertiary">
              Отложены — сделаны, но применения пока не нашли
            </Text>
            <div className={styles.chips}>
              {MECHANISMS.filter((m) => m.parked).map((m) => (
                <Chip
                  key={m.id}
                  active={mechanism === m.id}
                  onClick={() => setMechanism(m.id)}
                  title={m.note}
                >
                  {m.title}
                </Chip>
              ))}
            </div>

            <Text variant="caption" tone="tertiary">
              {mechanism
                ? MECHANISMS.find((m) => m.id === mechanism)?.note
                : 'Мешок: механизм не повторяется, пока не выпали все'}
            </Text>
          </Stack>

          <Stack gap={2}>
            <Text variant="overline" tone="sacred">
              Уровень
            </Text>
            <div className={styles.chips}>
              {SCOPES.map(([id, label]) => (
                <Chip key={id} active={scope === id} onClick={() => setScope(id)}>
                  {label}
                </Chip>
              ))}
            </div>
            <Text variant="caption" tone="tertiary">
              Экран звучит материалом, объект внутри экрана — глубиной.
              При «Случайно» набор берётся по уровню.
            </Text>
          </Stack>

          <Stack gap={2}>
            <Text variant="overline" tone="sacred">
              Темп
            </Text>
            <div className={styles.chips}>
              {SPEEDS.map(([label, value]) => (
                <Chip key={label} active={speed === value} onClick={() => setSpeed(value)}>
                  {label}
                </Chip>
              ))}
            </div>
          </Stack>

          <Stack gap={2}>
            <Text variant="overline" tone="sacred">
              Сид
            </Text>
            <div className={styles.seedRow}>
              <Text variant="body-m" tone="secondary" className={styles.seed}>
                {seed}
              </Text>
              <Button variant="ghost" size="sm" onClick={reseed}>
                Перебросить
              </Button>
            </div>
            <Text variant="caption" tone="tertiary">
              Один сид на сессию. Форма берётся из пула, а сдвиг, растяжение
              и снос свои на каждый показ — поэтому переходы не повторяются
              при одном сиде.
            </Text>
          </Stack>

          <Button onClick={replay} fullWidth>
            Проиграть ещё раз
          </Button>
        </Stack>
      </Surface>

      {/* Три площади сразу: механизм, хороший на блоке, может не работать
          на бейдже и разваливаться на карте. */}
      <Stack gap={5}>
        <Text variant="overline" tone="tertiary">
          Бейдж
        </Text>
        <InkReveal
          key={`badge-${take}`}
          show={shown}
          mechanism={mechanism}
          speed={speed}
          scope={scope}
          salt={1}
          onHidden={onHidden}
        >
          <span className={styles.badge}>Перевёрнутая</span>
        </InkReveal>

        <Text variant="overline" tone="tertiary">
          Блок текста
        </Text>
        <InkReveal key={`block-${take}`} show={shown} mechanism={mechanism} speed={speed} scope={scope} salt={2}>
          <Surface level="raised" sacred className={styles.block}>
            <div className="oracle">
              <p className="oracle__lead">
                Башня падает не потому, что её толкнули, — а потому, что она была
                построена на том, во что ты перестал верить.
              </p>
              <p className="oracle__question">Что рухнет, если ты перестанешь это держать?</p>
            </div>
          </Surface>
        </InkReveal>

        <Text variant="overline" tone="tertiary">
          Карта
        </Text>
        <InkReveal key={`card-${take}`} show={shown} mechanism={mechanism} speed={speed} scope={scope} salt={3}>
          <div className={styles.cardBox}>
            <Card back="/card-back.webp" face="/cards/tower.webp" label="Башня" size="xl" revealed />
          </div>
        </InkReveal>
      </Stack>
    </Stack>
  )
}

function Chip({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean
  onClick: () => void
  title?: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      className={styles.chip}
      aria-pressed={active}
      title={title}
      onClick={() => {
        haptic('select')
        onClick()
      }}
    >
      {children}
    </button>
  )
}
